/**
 * Base64 Encoding/Decoding Utilities with Proper UTF-8 Support
 * 
 * JavaScript's built-in atob() and btoa() only handle Latin-1 (ISO-8859-1) encoding,
 * which corrupts multi-byte UTF-8 characters like emojis.
 * 
 * These utilities properly handle UTF-8 encoding for all Unicode characters.
 */

/**
 * Decode base64 string to UTF-8 text with proper handling of multi-byte characters
 * Supports emojis and all Unicode characters
 * 
 * @param {string} base64String - Base64 encoded string
 * @returns {string} Decoded UTF-8 string
 * 
 * @example
 * // Decoding text with emojis
 * const encoded = 'SGVsbG8g8J+QuA=='; // "Hello 🐸"
 * const decoded = decodeBase64UTF8(encoded);
 * console.log(decoded); // "Hello 🐸"
 */
export function decodeBase64UTF8(base64String) {
  try {
    // Step 1: Decode base64 to binary string (Latin-1 encoding)
    const binaryString = atob(base64String);
    
    // Step 2: Convert binary string to byte array
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    
    // Step 3: Decode byte array as UTF-8
    const decoder = new TextDecoder('utf-8');
    return decoder.decode(bytes);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to decode base64 as UTF-8:', error);
    // Fallback to simple atob for backwards compatibility
    return atob(base64String);
  }
}

/**
 * Encode UTF-8 text to base64 string with proper handling of multi-byte characters
 * Supports emojis and all Unicode characters
 * 
 * @param {string} text - UTF-8 text to encode
 * @returns {string} Base64 encoded string
 * 
 * @example
 * // Encoding text with emojis
 * const text = 'Hello 🐸';
 * const encoded = encodeBase64UTF8(text);
 * console.log(encoded); // "SGVsbG8g8J+QuA=="
 */
export function encodeBase64UTF8(text) {
  try {
    // Step 1: Encode UTF-8 text to byte array
    const encoder = new TextEncoder();
    const bytes = encoder.encode(text);
    
    // Step 2: Convert byte array to binary string
    let binaryString = '';
    for (let i = 0; i < bytes.length; i++) {
      binaryString += String.fromCharCode(bytes[i]);
    }
    
    // Step 3: Encode binary string to base64
    return btoa(binaryString);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to encode UTF-8 as base64:', error);
    // Fallback to simple btoa for backwards compatibility
    return btoa(text);
  }
}

/**
 * Test if a string contains multi-byte UTF-8 characters (emojis, etc.)
 * 
 * @param {string} text - Text to test
 * @returns {boolean} True if text contains multi-byte characters
 */
export function hasMultibyteChars(text) {
  // Check if any character has a code point > 255 (outside Latin-1 range)
  for (let i = 0; i < text.length; i++) {
    if (text.charCodeAt(i) > 255) {
      return true;
    }
  }
  return false;
}

const base64Utils = {
  decodeBase64UTF8,
  encodeBase64UTF8,
  hasMultibyteChars
};

export default base64Utils;
